"use client";

import { Box, Center, HStack, SimpleGrid, Text } from "@chakra-ui/react";
import type { MindstringPrototype } from "@/generated/fastapi";
import {
  mindstringPrototypesSlang,
  type MindstringPrototypeUiMetadataForFrontend,
} from "@/shared/config/backend/mindstrings";
import { getPrototypeTone } from "@/shared/config/prototypeTheme";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";
import ProfileYourselfButton from "./ProfileYourselfButton";

type IntroConceptAction = {
  href: string;
  icon?: GenericIconType;
  label: string;
  tone: "primary";
};

type IntroConceptsProps = {
  action: IntroConceptAction;
};

type ConceptPlacement = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

type IntroConcept = {
  prototype: MindstringPrototype;
  placement: ConceptPlacement;
  prompt: string;
};

const INTRO_CONCEPTS = [
  {
    prototype: "job_activity",
    placement: "topLeft",
    prompt: "what you can do",
  },
  {
    prototype: "learning_outcome",
    placement: "topRight",
    prompt: "what you know about",
  },
  {
    prototype: "workplace_characteristic",
    placement: "bottomLeft",
    prompt: "where you thrive",
  },
  {
    prototype: "individual_description",
    placement: "bottomRight",
    prompt: "how you see yourself",
  },
] as const satisfies readonly IntroConcept[];

const PLACEMENT_STYLES = {
  topLeft: {
    gridArea: { base: "auto", md: "topLeft" },
    justifySelf: { base: "stretch", md: "start" },
    alignSelf: { base: "stretch", md: "start" },
  },
  topRight: {
    gridArea: { base: "auto", md: "topRight" },
    justifySelf: { base: "stretch", md: "end" },
    alignSelf: { base: "stretch", md: "start" },
  },
  bottomLeft: {
    gridArea: { base: "auto", md: "bottomLeft" },
    justifySelf: { base: "stretch", md: "start" },
    alignSelf: { base: "stretch", md: "end" },
  },
  bottomRight: {
    gridArea: { base: "auto", md: "bottomRight" },
    justifySelf: { base: "stretch", md: "end" },
    alignSelf: { base: "stretch", md: "end" },
  },
} as const;

const CONCEPT_SHAPES = {
  topLeft: {
    rounded: "full",
    transform: "rotate(5deg)",
    compactTransform: "rotate(4deg)",
    inset: { base: "1", md: "0" },
  },
  topRight: {
    rounded: { base: "lg", md: "xl" },
    transform: "rotate(-3deg)",
    compactTransform: "rotate(-2deg)",
    inset: { base: "0", md: "-1" },
  },
  bottomLeft: {
    rounded: "lg",
    transform: "rotate(-10deg)",
    compactTransform: "rotate(-7deg)",
    inset: { base: "0", md: "-1" },
  },
  bottomRight: {
    rounded: "full",
    transform: "rotate(6deg)",
    compactTransform: "rotate(4deg)",
    inset: { base: "1", md: "0" },
  },
} as const;

const CONCEPT_ARROWS = [
  {
    prototype: "job_activity",
    path: "M 28 35 L 41.5 49",
  },
  {
    prototype: "learning_outcome",
    path: "M 72 35 L 58.5 49",
  },
  {
    prototype: "workplace_characteristic",
    path: "M 28 65 L 41.5 51",
  },
  {
    prototype: "individual_description",
    path: "M 72 65 L 58.5 51",
  },
] as const satisfies readonly {
  prototype: MindstringPrototype;
  path: string;
}[];

const MOBILE_CONCEPT_LINES = [
  {
    prototype: "job_activity",
    path: "M 25 20 L 42 47",
  },
  {
    prototype: "learning_outcome",
    path: "M 75 20 L 58 47",
  },
  {
    prototype: "workplace_characteristic",
    path: "M 25 80 L 42 53",
  },
  {
    prototype: "individual_description",
    path: "M 75 80 L 58 53",
  },
] as const satisfies readonly {
  prototype: MindstringPrototype;
  path: string;
}[];

function ConceptLineLayer({
  lines,
  display,
}: {
  lines: readonly {
    prototype: MindstringPrototype;
    path: string;
  }[];
  display: { base: "none" | "block"; md: "none" | "block" };
}) {
  return (
    <>
      {lines.map((line) => {
        const tone = getPrototypeTone(line.prototype);

        return (
          <Box
            key={line.prototype}
            display={display}
            position="absolute"
            inset="0"
            w="full"
            h="full"
            color={tone.accent}
            pointerEvents="none"
            aria-hidden="true"
            zIndex="0"
          >
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              width="100%"
              height="100%"
            >
              <path
                d={line.path}
                fill="none"
                stroke="currentColor"
                strokeWidth="0.55"
                strokeLinecap="round"
                strokeDasharray="2 1.4"
                opacity="0.46"
              />
            </svg>
          </Box>
        );
      })}
    </>
  );
}

function IntroConceptCard({
  concept,
  compact = false,
}: {
  concept: IntroConcept;
  compact?: boolean;
}) {
  const metadata: MindstringPrototypeUiMetadataForFrontend =
    mindstringPrototypesSlang[concept.prototype];
  const tone = getPrototypeTone(concept.prototype);
  const placement = PLACEMENT_STYLES[concept.placement];
  const shape = CONCEPT_SHAPES[concept.placement];
  const isLeftSide =
    concept.placement === "topLeft" || concept.placement === "bottomLeft";

  return (
    <Box
      role="listitem"
      position="relative"
      isolation="isolate"
      gridArea={placement.gridArea}
      justifySelf={placement.justifySelf}
      alignSelf={placement.alignSelf}
      w={compact ? "full" : { base: "full", md: "[15rem]", lg: "[17rem]" }}
      maxW={compact ? "full" : { base: "full", md: "[17rem]" }}
      color="gray.950"
      minH={compact ? "[4.65rem]" : { base: "auto", md: "[10.75rem]" }}
      p={compact ? "2" : { base: "4", md: "5" }}
      transition="transform 160ms ease"
      _hover={{
        transform: "translateY(-2px)",
      }}
    >
      <Box
        position="absolute"
        inset={shape.inset}
        rounded={shape.rounded}
        bg={tone.accentBg}
        opacity={compact ? "0.82" : "0.66"}
        transform={compact ? shape.compactTransform : shape.transform}
        aria-hidden="true"
        zIndex="-1"
      />

      <HStack
        align="center"
        gap={compact ? "1.5" : "3"}
        flexDirection={isLeftSide ? "row-reverse" : "row"}
        justify={isLeftSide ? "end" : "start"}
      >
        <Center
          flexShrink="0"
          h={compact ? "6" : "10"}
          w={compact ? "6" : "10"}
          rounded="full"
          bg="moveon.public.surface.base"
          color={tone.accent}
          boxShadow="sm"
        >
          <GenericIcon
            icon={metadata.icon}
            size={compact ? "xs" : "sm"}
            color={tone.accent}
          />
        </Center>

        <Box minW="0" textAlign={isLeftSide ? "right" : "left"}>
          <Text
            fontSize={compact ? "[0.63rem]" : "xs"}
            fontWeight="bold"
            textTransform="uppercase"
            color={tone.accent}
            whiteSpace={compact ? "nowrap" : undefined}
            lineHeight="shorter"
          >
            {metadata.label}
          </Text>
          <Text
            fontSize={compact ? "sm" : { base: "md", md: "lg" }}
            fontWeight="extrabold"
            lineHeight="short"
          >
            {concept.prompt}
          </Text>
        </Box>
      </HStack>

      {compact ? null : (
        <Text
          mt="3"
          color="moveon.publicFont"
          fontSize="sm"
          lineHeight="tall"
          textAlign={isLeftSide ? "right" : "left"}
        >
          {metadata.description}
        </Text>
      )}
    </Box>
  );
}

function CenterAction({ action }: IntroConceptsProps) {
  return (
    <Box textAlign="center">
      <ProfileYourselfButton
        href={action.href}
        icon={action.icon}
        label={action.label}
        tone={action.tone}
      />
    </Box>
  );
}

function MobileConceptMap({ action }: IntroConceptsProps) {
  return (
    <Box
      display={{ base: "block", md: "none" }}
      position="relative"
      role="list"
      aria-label="Profile concepts"
    >
      <ConceptLineLayer
        lines={MOBILE_CONCEPT_LINES}
        display={{ base: "block", md: "none" }}
      />

      <SimpleGrid
        position="relative"
        zIndex="1"
        columns={2}
        gap="3"
        gridTemplateAreas={`"topLeft topRight" "center center" "bottomLeft bottomRight"`}
        alignItems="center"
      >
        {INTRO_CONCEPTS.map((concept) => (
          <IntroConceptCard key={concept.prototype} concept={concept} compact />
        ))}

        <Center gridArea="center" py="4">
          <CenterAction action={action} />
        </Center>
      </SimpleGrid>
    </Box>
  );
}

function DesktopConceptMap({ action }: IntroConceptsProps) {
  return (
    <Box
      display={{ base: "none", md: "block" }}
      position="relative"
      role="list"
      aria-label="Profile concepts"
    >
      <Box
        position="absolute"
        inset="50% auto auto 50%"
        h={{ md: "[22rem]", lg: "[25rem]" }}
        w={{ md: "[22rem]", lg: "[25rem]" }}
        transform="translate(-50%, -50%) rotate(45deg)"
        rounded="md"
        bg="moveon.prototype.learningOutcome.bg"
        opacity="0.28"
        aria-hidden="true"
      />

      <ConceptLineLayer
        lines={CONCEPT_ARROWS}
        display={{ base: "none", md: "block" }}
      />

      <SimpleGrid
        position="relative"
        zIndex="1"
        columns={{ base: 1, md: 3 }}
        columnGap={{ base: "4", md: "6", lg: "8" }}
        rowGap={{ base: "4", md: "10", lg: "14" }}
        gridTemplateAreas={{
          md: `"topLeft center topRight" "bottomLeft center bottomRight"`,
        }}
        alignItems="center"
      >
        {INTRO_CONCEPTS.map((concept) => (
          <IntroConceptCard key={concept.prototype} concept={concept} />
        ))}

        <Center
          gridArea={{ base: "auto", md: "center" }}
          minH={{ base: "[9rem]", md: "[15rem]", lg: "[17rem]" }}
        >
          <CenterAction action={action} />
        </Center>
      </SimpleGrid>
    </Box>
  );
}

export default function IntroConcepts({ action }: IntroConceptsProps) {
  return (
    <Box
      mx={{ base: "auto", md: "8", lg: "20" }}
      maxW="[1120px]"
      position="relative"
    >
      <MobileConceptMap action={action} />
      <DesktopConceptMap action={action} />
    </Box>
  );
}
